import Splide from '@splidejs/splide';
import { initSliderControls } from '../utils/slider-controls';

const initProductTabs = (component) => {
  if (component.dataset.productTabsReady === 'true') return;

  const tabs = Array.from(component.querySelectorAll('[role="tab"]'));
  const panels = Array.from(component.querySelectorAll('[role="tabpanel"]'));
  const sideBadge = component.querySelector('.home-products__side-badge');
  const states = panels.map((panel) => {
    const element = panel.querySelector('[data-product-scroller]');

    return {
      panel,
      element,
      track: element?.querySelector('.splide__track'),
      slides: Array.from(
        element?.querySelector('.splide__list')?.children || []
      ),
      progress: panel.querySelector('.home-products__progress'),
      progressBar: panel.querySelector('[data-product-progress]'),
      progressThumb: panel.querySelector('[data-product-thumb]'),
      slider: null,
      controls: null,
      canLoop: false,
      lastWidth: 0,
    };
  });

  if (!tabs.length || !panels.length) return;

  let resizeFrame = null;

  const activeState = () => states.find(({ panel }) => !panel.hidden);

  const syncSideBadge = () => {
    if (!sideBadge || window.matchMedia('(max-width: 1023px)').matches) {
      component.style.removeProperty('--side-badge-top');
      component.style.removeProperty('--side-badge-height');
      sideBadge?.classList.remove('is-position-synced');
      return;
    }

    const card = activeState()?.slides[0]?.querySelector('.product-card');
    if (!card) return;

    const componentRect = component.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    component.style.setProperty(
      '--side-badge-top',
      `${cardRect.top - componentRect.top}px`
    );
    component.style.setProperty('--side-badge-height', `${cardRect.height}px`);
    sideBadge.classList.add('is-position-synced');
  };

  const getViewportWidth = (state) => {
    const style = window.getComputedStyle(state.track);

    return (
      state.track.clientWidth -
      parseFloat(style.paddingLeft) -
      parseFloat(style.paddingRight)
    );
  };

  const hasOverflow = (state) => {
    if (!state.track || state.slides.length < 2) return false;

    const slideWidth = state.slides.reduce(
      (width, slide) => width + slide.getBoundingClientRect().width,
      0
    );

    return slideWidth > getViewportWidth(state) + 1;
  };

  const destroySlider = (state) => {
    state.controls?.destroy();
    state.slider?.destroy(true);
    state.controls = null;
    state.slider = null;
  };

  const mountSlider = (state) => {
    if (!state.element || !state.slides.length) return;

    destroySlider(state);
    state.canLoop = hasOverflow(state);
    state.progress?.classList.toggle('is-disabled', !state.canLoop);
    state.slider = new Splide(state.element, {
      arrows: false,
      autoWidth: true,
      drag: state.canLoop,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      gap: 0,
      keyboard: 'focused',
      pagination: false,
      perMove: 1,
      type: state.canLoop ? 'loop' : 'slide',
    });

    state.controls = initSliderControls({
      section: component,
      slider: state.slider,
      slides: state.slides,
      progressBar: state.progressBar,
      progressThumb: state.progressThumb,
      interval: 4500,
      speed: 700,
      loop: state.canLoop,
      getSlideLabel: (slide) =>
        slide?.querySelector('.product-card__title')?.textContent.trim(),
    });

    state.slider.mount();
    state.lastWidth = getViewportWidth(state);
    document.dispatchEvent(new Event('holytaste:wishlist-sync'));
  };

  const refreshActiveSlider = () => {
    const state = activeState();
    if (!state) return;

    if (!state.slider || state.canLoop !== hasOverflow(state)) {
      mountSlider(state);
    } else {
      const width = getViewportWidth(state);
      if (Math.abs(width - state.lastWidth) > 1) state.slider.refresh();
      state.lastWidth = width;
    }
    syncSideBadge();
  };

  const requestRefresh = () => {
    if (resizeFrame !== null) return;

    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = null;
      refreshActiveSlider();
    });
  };

  // Horizontal trackpad swipes step the slider one card at a time.
  states.forEach((state) => {
    let lastWheel = 0;

    state.element?.addEventListener(
      'wheel',
      (event) => {
        if (
          !state.canLoop ||
          Math.abs(event.deltaX) <= Math.abs(event.deltaY)
        ) {
          return;
        }

        event.preventDefault();
        if (Date.now() - lastWheel < 650) return;

        lastWheel = Date.now();
        state.slider?.go(event.deltaX > 0 ? '>' : '<');
      },
      { passive: false }
    );
  });

  const activateTab = (nextTab, setFocus = true) => {
    tabs.forEach((tab) => {
      const isActive = tab === nextTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });

    states.forEach((state) => {
      destroySlider(state);
      state.panel.hidden =
        state.panel.id !== nextTab.getAttribute('aria-controls');
    });

    window.requestAnimationFrame(refreshActiveSlider);

    if (setFocus) nextTab.focus();
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => activateTab(tab, false));
    tab.addEventListener('keydown', (event) => {
      let nextIndex = null;

      if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft')
        nextIndex = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;

      if (nextIndex !== null) {
        event.preventDefault();
        activateTab(tabs[nextIndex]);
      }
    });
  });

  window.addEventListener('resize', requestRefresh);
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(requestRefresh);
    observer.observe(component);
  }

  component.dataset.productTabsReady = 'true';
  window.requestAnimationFrame(refreshActiveSlider);
};

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-tabs]').forEach(initProductTabs);
});
